import { useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import Sync from "@mui/icons-material/Sync";
import Search from "@mui/icons-material/Search";
import type { ReconciliationPreview, ReconciliationSelection } from "./models";
import { DIFFERENCE_TYPES } from "./models";
import { ComparisonValue } from "./ComparisonValue";
import { MatchOptionLabel } from "./MatchOptionLabel";
import { ReferenceName } from "./ReferenceNames";
import {
	applyReconciliation,
	ComparisonRequestError,
	previewReconciliation,
} from "./repository";

const rowLabel = (count: number) => `${count} ${count === 1 ? "row" : "rows"}`;

export function ReconciliationDialog({
	selection,
	onClose,
	onInvalidate,
}: {
	selection: ReconciliationSelection;
	onClose: () => void;
	onInvalidate: (message: string, success: boolean) => void;
}) {
	const [preview, setPreview] = useState<ReconciliationPreview | null>(null);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(true);
	const [applying, setApplying] = useState(false);
	const [attempted, setAttempted] = useState(false);
	const [page, setPage] = useState(0);
	const [search, setSearch] = useState("");
	const [searchText, setSearchText] = useState("");
	const [includeReferencedNames, setIncludeReferencedNames] = useState(false);
	const inserting = selection.operation === "insert";
	const creatableReferences = (
		selection.result.examples["Referenced names"] ?? []
	).filter(
		({ reference }) =>
			reference?.missingSourceCatalog &&
			!reference.targetId &&
			reference.canCreate,
	).length;
	const submitted = useRef(false);
	const invalidate = useRef(onInvalidate);
	invalidate.current = onInvalidate;

	useEffect(() => {
		const controller = new AbortController();
		previewReconciliation(
			selection.result.snapshotToken ?? "",
			selection.type,
			selection.key,
			selection.operation,
			controller.signal,
			page,
			search,
			selection.matchOption?.id,
			includeReferencedNames,
		)
			.then((result) => {
				if (!controller.signal.aborted) setPreview(result);
			})
			.catch((reason: unknown) => {
				if (controller.signal.aborted) return;
				const message =
					reason instanceof Error
						? reason.message
						: "Could not prepare the changes.";
				setError(message);
				if (reason instanceof ComparisonRequestError && reason.requiresRerun)
					invalidate.current(message, false);
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});
		return () => controller.abort();
	}, [selection, page, search, includeReferencedNames]);

	function changePreview(nextPage: number, nextSearch: string) {
		if (nextPage === page && nextSearch === search) return;
		setLoading(true);
		setError("");
		setPage(nextPage);
		setSearch(nextSearch);
	}

	async function apply() {
		if (!preview || preview.rowCount === 0 || submitted.current) return;
		submitted.current = true;
		setApplying(true);
		setAttempted(true);
		try {
			const result = await applyReconciliation(
				preview.confirmationToken,
				new AbortController().signal,
			);
			onInvalidate(
				`${result.insertedRows} rows inserted${result.createdReferences > 0 ? ` (${result.createdReferences} referenced names created and linked)` : ""} and ${result.updatedRows} rows updated in MES; ${result.skippedRows} skipped; ${result.failedRows} failed.`,
				true,
			);
			onClose();
		} catch (reason) {
			const message =
				reason instanceof Error
					? reason.message
					: "The operation result could not be confirmed.";
			setError(message);
			onInvalidate(
				`${message} Run comparison again before another change.`,
				false,
			);
		} finally {
			setApplying(false);
		}
	}

	return (
		<Dialog
			open
			fullWidth
			maxWidth="md"
			onClose={applying ? undefined : onClose}
			disableEscapeKeyDown={applying}
		>
			<DialogTitle>
				{selection.key
					? `${inserting ? "Insert in MES" : "Update MES"}: ${selection.key}`
					: inserting
						? "Insert in MES from Rollshop"
						: "Update MES from Rollshop"}
			</DialogTitle>
			<DialogContent>
				<Alert severity="info" sx={{ mb: 2 }}>
					{inserting
						? "Create the missing MES records from Rollshop. Unavailable optional catalog links are left empty. Required references are checked before insertion."
						: selection.result.concept === "roll-cuts"
							? "Update MES from Rollshop for cuts with exactly equal diameters and dates within ±3 calendar days. Dates and comments, including empty comments, can be replaced. MES time of day is retained. Cuts with different diameters are excluded."
							: "Rollshop → MES only. Rollshop will not be changed. Richer MES values and protected fields are preserved. Unavailable catalog references are left unchanged while other eligible fields are updated."}
					{inserting && selection.result.concept === "rolls" && (
						<>
							{" "}
							Active rolls take the MES default status; archived rolls keep
							their Scrapped or Broken status from Rollshop. Roll assignments
							are never imported here.
						</>
					)}
				</Alert>
				{inserting && selection.key === null && creatableReferences > 0 && (
					<Box sx={{ mb: 2 }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={includeReferencedNames}
									disabled={loading || applying || attempted}
									onChange={(_, checked) => {
										setLoading(true);
										setError("");
										setPreview(null);
										setPage(0);
										setIncludeReferencedNames(checked);
									}}
								/>
							}
							label={`Include referenced names (${creatableReferences})`}
						/>
						<Typography variant="body2" color="text.secondary">
							Create missing names in this MES catalog using its defaults, even
							when Rollshop has no catalog record, and save their reference
							links. Names already linked or present in MES are excluded.
						</Typography>
					</Box>
				)}
				{(loading || applying) && (
					<Box role="status" sx={{ mb: 2 }}>
						<Typography>
							{applying
								? inserting
									? "Inserting in MES. Please wait…"
									: "Updating MES. Please wait…"
								: "Preparing changes…"}
						</Typography>
						<LinearProgress
							aria-label={
								applying ? "Applying changes in MES" : "Preparing changes"
							}
						/>
					</Box>
				)}
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}
				{preview && (
					<>
						<Typography fontWeight={600}>
							{preview.concept} ·{" "}
							{includeReferencedNames
								? "Catalog records + referenced names"
								: (DIFFERENCE_TYPES.find(
										(option) => option.key === preview.type,
									)?.label ?? preview.type)}
						</Typography>
						<Typography variant="body2">{preview.scope}</Typography>
						{selection.result.scope.startsWith("Selected") && (
							<Typography variant="body2">
								{preview.from.replace("T", " ")} →{" "}
								{preview.to.replace("T", " ")} ({preview.timeZone})
							</Typography>
						)}
						<Typography sx={{ my: 2 }} role="status">
							{inserting && includeReferencedNames ? (
								<>
									{preview.rowCount - preview.referenceRowCount} catalog records
									will be inserted and {preview.referenceRowCount} referenced
									names will be created and linked in MES.{" "}
								</>
							) : (
								<>
									{rowLabel(preview.rowCount)} will be{" "}
									{inserting ? "inserted in MES" : "updated"}.{" "}
								</>
							)}
							{rowLabel(preview.skippedRows)} will be skipped.
						</Typography>
						{!selection.key && (
							<Typography variant="body2" sx={{ mb: 2 }}>
								Affected fields:{" "}
								{Object.entries(preview.affectedFields)
									.map(([field, count]) => `${field} (${count})`)
									.join(", ") || "None"}
								.
							</Typography>
						)}
						{!selection.key && preview.rowCount > 0 && (
							<Box
								sx={{
									mb: 2,
									py: 1,
									position: "sticky",
									top: 0,
									bgcolor: "background.paper",
									zIndex: 1,
								}}
							>
								<Stack
									component="form"
									direction="row"
									gap={1}
									onSubmit={(event) => {
										event.preventDefault();
										if (!loading && !applying && !attempted)
											changePreview(0, searchText.trim());
									}}
								>
									<TextField
										size="small"
										label={
											selection.result.concept === "purchase-orders"
												? "Find PO or item"
												: "Find item"
										}
										value={searchText}
										onChange={(event) => setSearchText(event.target.value)}
										disabled={loading || applying || attempted}
										slotProps={{ htmlInput: { maxLength: 2000 } }}
										sx={{ flex: 1 }}
									/>
									<Button
										type="submit"
										variant="outlined"
										startIcon={<Search />}
										disabled={loading || applying || attempted}
									>
										Search
									</Button>
									<Button
										variant="outlined"
										disabled={
											loading ||
											applying ||
											attempted ||
											!(searchText || search)
										}
										onClick={() => {
											setSearchText("");
											changePreview(0, "");
										}}
									>
										Clear
									</Button>
								</Stack>
								<Typography variant="caption" color="text.secondary">
									All {preview.rowCount} planned rows are included when you
									confirm, regardless of the search or page.
								</Typography>
								<TablePagination
									component="div"
									count={preview.matchingRows}
									page={preview.page}
									rowsPerPage={preview.pageSize}
									rowsPerPageOptions={[preview.pageSize]}
									onPageChange={(_, nextPage) =>
										changePreview(nextPage, search)
									}
									disabled={loading || applying || attempted}
								/>
							</Box>
						)}
						{search && preview.matchingRows === 0 && (
							<Typography sx={{ mb: 2 }}>
								No planned rows match this search.
							</Typography>
						)}
						{preview.examples.map((row) => (
							<Box key={row.key} sx={{ mb: 2 }}>
								<Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
									{row.createsReference ? (
										<ReferenceName name={row.key} />
									) : (
										row.key
									)}
								</Typography>
								{row.createsReference && (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 0.5 }}
									>
										Create this referenced name with catalog defaults and link
										it to the new MES record.
									</Typography>
								)}
								{row.matchOption && (
									<MatchOptionLabel option={row.matchOption} />
								)}
								<Table
									size="small"
									aria-label={`Proposed changes for ${row.key}`}
									sx={{ tableLayout: "fixed" }}
								>
									<TableHead>
										<TableRow>
											<TableCell>Field</TableCell>
											<TableCell>Current MES</TableCell>
											<TableCell>New value from Rollshop</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{row.changes.map((change) => (
											<TableRow key={change.field}>
												<TableCell sx={{ overflowWrap: "anywhere" }}>
													{change.field}
												</TableCell>
												<TableCell
													sx={{
														whiteSpace: "pre-wrap",
														overflowWrap: "anywhere",
													}}
												>
													{inserting ? (
														"-"
													) : (
														<ComparisonValue value={change.mes} />
													)}
												</TableCell>
												<TableCell
													sx={{
														whiteSpace: "pre-wrap",
														overflowWrap: "anywhere",
													}}
												>
													<ComparisonValue value={change.rollshop} />
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
								{row.preserved.length > 0 && (
									<Box
										component="details"
										sx={{ mt: 0.5, color: "text.secondary" }}
									>
										<Typography component="summary" variant="caption">
											{row.preserved.length} fields kept or left empty
										</Typography>
										{row.preserved.map((field) => (
											<Typography key={field} variant="caption" display="block">
												{field}
											</Typography>
										))}
									</Box>
								)}
							</Box>
						))}
						{preview.exclusions.map((row) => (
							<Typography key={row.key} variant="body2" sx={{ mb: 1 }}>
								{row.key}: {row.reason}
							</Typography>
						))}
						{preview.skippedRows > preview.exclusions.length && (
							<Typography variant="caption">
								Showing {preview.exclusions.length} of {preview.skippedRows}{" "}
								skipped rows.
							</Typography>
						)}
						{preview.rowCount === 0 && (
							<Alert severity="info">
								{inserting &&
								selection.key === null &&
								!includeReferencedNames &&
								creatableReferences > 0
									? "No catalog records are eligible for insertion. Select Include referenced names to create and link the missing names."
									: "There are no eligible changes to apply."}
							</Alert>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" disabled={applying} onClick={onClose}>
					{attempted ? "Close" : "Cancel"}
				</Button>
				<Button
					variant="contained"
					color={inserting ? "success" : "primary"}
					startIcon={inserting ? <AddCircleOutline /> : <Sync />}
					disabled={
						loading || applying || attempted || !!error || !preview?.rowCount
					}
					onClick={apply}
				>
					Confirm {inserting ? "insert" : "update"} of{" "}
					{rowLabel(preview?.rowCount ?? 0)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
